import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import {
  LIST_ACTIONS,
  PERSONALIZATION_DEFAULT_CONFIG,
  STATUS_TO_TAB_IN_ACTION,
  TABS_FILTERS,
  violationsHome,
} from '../../../shared/constants';
import { usePersonalization, useTypedSelector } from '../../../shared/hooks';
import {
  detailsActions,
  violationsTabActions,
} from '../../../shared/store/actions';
import { useLazyGetSummaryDetailsQuery } from '../../../shared/store/globalApis';
import {
  type ListRowAccountState,
  type SodSidePanelSummaryData,
  type TrackedFunctionData,
} from '../../../shared/types';
import { createDevRejectionHandler } from '../../../shared/utils';

const FUNCTION_KEYS = [
  'function1',
  'function2',
  'function3',
  'function4',
  'function5',
] as const;

const isValidTabInAction = (tab: string): boolean =>
  Object.values(TABS_FILTERS).some((entry) => entry.value === tab);

const mapSummaryAccounts = (
  accounts: SodSidePanelSummaryData['violationData']['accounts']
): ListRowAccountState[] =>
  accounts.map((account, index) => ({
    accountKey: index,
    accountName: account.accountName,
    accountDisplayName: account.accountDisplayName,
    endpointName: '',
    endpointLogo: null,
  }));

const buildFunctionsFromRisk = (
  risk: SodSidePanelSummaryData['violationData']['risk']
): TrackedFunctionData[] =>
  FUNCTION_KEYS.map((key, index) => {
    const fn = risk[key];

    if (!fn) {
      return undefined;
    }

    return { key: index + 1, ...fn };
  }).filter((fn): fn is TrackedFunctionData => fn !== undefined);

type SummaryDetailsPayload = Pick<
  SodSidePanelSummaryData,
  'violationData' | 'latestActivity'
>;

type ViolationSummaryData = SodSidePanelSummaryData['violationData'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isSummaryDetailsPayload = (
  value: unknown
): value is SummaryDetailsPayload =>
  isRecord(value) && isRecord(value.violationData);

const isHydratableViolationData = (
  violationData: unknown
): violationData is ViolationSummaryData => {
  if (!isRecord(violationData)) {
    return false;
  }

  const { status, user, userCapability, risk, accounts } = violationData;

  return (
    typeof status === 'string' &&
    isRecord(user) &&
    typeof user.username === 'string' &&
    isRecord(userCapability) &&
    typeof userCapability.readOnly === 'boolean' &&
    Array.isArray(userCapability.allowedActions) &&
    isRecord(risk) &&
    Array.isArray(accounts)
  );
};

const hasActionContextForViolation = (
  actionManager: {
    sodKey?: string;
    allowedActions?: string[];
  } | null,
  violationId: string
): boolean =>
  actionManager?.sodKey === violationId &&
  Boolean(actionManager.allowedActions?.length);

/**
 * Restores violations details header chip and footer actions after reload by
 * rehydrating tabInAction from personalization (#2) and action context from
 * getSummaryDetails (#1).
 */
const useHydrateViolationsDetailsSession = (
  qId: string | undefined,
  source: string | null
): void => {
  const dispatch = useDispatch();
  const { getPersonlization } = usePersonalization();
  const [fetchSummary] = useLazyGetSummaryDetailsQuery();
  const hydratedQIdRef = useRef<string | null>(null);

  const tabInAction = useTypedSelector(
    (state) => state.aag.violationsTab.controls.tabInAction
  );
  const actionManager = useTypedSelector(
    (state) => state.aag.details.violationsTab.actionManager
  );
  const navigationTitle = useTypedSelector(
    (state) => state.aag.details.navigationContext.title
  );
  const navigationUserName = useTypedSelector(
    (state) => state.aag.details.navigationContext.userName
  );

  const isViolationsDetails = source === violationsHome;

  // #2: Restore list tab from personalization until API status is available.
  useEffect(() => {
    if (!isViolationsDetails || !qId || tabInAction !== null) {
      return;
    }

    const personalization = getPersonlization();
    const savedTab =
      PERSONALIZATION_DEFAULT_CONFIG.saveTab && personalization?.tab
        ? personalization.tab
        : null;

    if (savedTab && isValidTabInAction(savedTab)) {
      dispatch(violationsTabActions.setTabInAction(savedTab));
    }
  }, [isViolationsDetails, qId, tabInAction, dispatch, getPersonlization]);

  // #1: Fetch violation summary and populate Redux action/navigation context.
  useEffect(() => {
    if (!isViolationsDetails || !qId) {
      return;
    }

    if (hasActionContextForViolation(actionManager, qId)) {
      return;
    }

    if (hydratedQIdRef.current !== null && hydratedQIdRef.current !== qId) {
      hydratedQIdRef.current = null;
    }

    if (hydratedQIdRef.current === qId) {
      return;
    }

    hydratedQIdRef.current = qId;

    const hydrate = async (): Promise<void> => {
      try {
        const response = await fetchSummary(qId).unwrap();

        if (
          !isSummaryDetailsPayload(response) ||
          !isHydratableViolationData(response.violationData)
        ) {
          hydratedQIdRef.current = null;

          return;
        }

        const { violationData } = response;
        const { status, user, userCapability, risk, accounts } = violationData;
        const statusTab = STATUS_TO_TAB_IN_ACTION[status];

        if (statusTab) {
          dispatch(violationsTabActions.setTabInAction(statusTab));
        }

        const riskMeta = risk as typeof risk & {
          riskId?: string;
          riskName?: string;
        };
        const title =
          riskMeta.riskName ?? risk.riskDescription ?? risk.ruleset ?? '';

        dispatch(
          detailsActions.setDetailsNavigation({
            source: violationsHome,
            title: navigationTitle ?? title,
            userName: navigationUserName ?? user.username,
          })
        );

        dispatch(
          detailsActions.setViolationTabdetailsActionManager({
            isOpen: false,
            sodKey: qId,
            action: LIST_ACTIONS.VIEWDETAILS.value,
            title,
            userName: user.username,
            displayName: user.displayName,
            isReadOnlyActions: userCapability.readOnly,
            allowedActions: userCapability.allowedActions,
            riskId: riskMeta.riskId,
            rulesetName: risk.ruleset ?? undefined,
            mitigatingControlCount: 0,
            functions: buildFunctionsFromRisk(risk),
            accounts: mapSummaryAccounts(accounts),
            startDate: null,
            endDate: null,
          })
        );
      } catch (err: unknown) {
        hydratedQIdRef.current = null;
        throw err;
      }
    };

    hydrate().catch(
      createDevRejectionHandler('useHydrateViolationsDetailsSession')
    );
  }, [
    isViolationsDetails,
    qId,
    actionManager,
    dispatch,
    fetchSummary,
    navigationTitle,
    navigationUserName,
  ]);
};

// eslint-disable-next-line import/prefer-default-export
export { useHydrateViolationsDetailsSession };
